import { useQuery } from "@apollo/client";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  List,
  ListItem,
  Box,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { ReactElement } from "react";

import {
  GetLinkShortenerDomainQuery,
  GetLinkShortenerDomainQueryVariables,
  LinkShortenerDomainStatus,
  RetryDomainFinalLinkTestMutation,
  RetryDomainFinalLinkTestMutationVariables,
  UpdateLinkShortenerDomainMutationVariables,
  VerifyLinkShortenerDomainDnsAndCompleteSetupMutation,
  VerifyLinkShortenerDomainDnsAndCompleteSetupMutationVariables,
} from "~frontend/__generated__/graphql";
import ClickToCopy from "~frontend/components/ClickToCopy";
import Loading from "~frontend/components/Loading";
import {
  GET_DOMAIN_DETAIL,
  RETRY_DOMAIN_FINAL_LINK_TEST,
  VERIFY_LINK_SHORTENER_DOMAIN_DNS_AND_COMPLETE_SETUP,
} from "~frontend/graphql/domains";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import RequestOrEditDomain from "./RequestOrEditDomain";
import { Domain } from "./types";

function ErrorDialogMessage({
  message,
  messageDetails,
  nameservers,
  onClose,
}: {
  message: string;
  messageDetails?: string | null;
  nameservers?: string[] | null;
  onClose: () => void;
}): ReactElement {
  return (
    <>
      <DialogContent>
        <Alert severity="error">{message}</Alert>
        {messageDetails && <Typography>{messageDetails}</Typography>}
        {nameservers && (
          <>
            <Typography variant="h2" sx={{ mt: 2 }}>
              DNS Records
            </Typography>
            <NameServerList nameservers={nameservers} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </>
  );
}

function LoadingDialog({
  message,
  onClose,
}: {
  message: string;
  onClose: () => void;
}): ReactElement {
  return (
    <>
      <DialogContent>
        <Loading
          message={
            <>
              {message}
              <br />
              <br />
              This may take a few minutes. You can close this dialog and check
              back later.
            </>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </>
  );
}

function NameServerList({
  nameservers,
}: {
  nameservers: string[];
}): ReactElement {
  return (
    <List>
      {nameservers.map(ns => (
        <ListItem key={ns}>
          <ClickToCopy text={ns}>
            <Typography display="inline" fontFamily="monospace">
              {ns}
            </Typography>
          </ClickToCopy>
        </ListItem>
      ))}
    </List>
  );
}

function HealthCheck({ domain }: { domain: Domain }): ReactElement {
  const { data, loading, error } = useQuery<
    GetLinkShortenerDomainQuery,
    GetLinkShortenerDomainQueryVariables
  >(GET_DOMAIN_DETAIL, {
    variables: {
      input: {
        linkShortenerDomainId: domain.id,
      },
    },
    fetchPolicy: "network-only",
  });

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  if (data!.getLinkShortenerDomain.healthCheckErrors.length === 0) {
    return <Alert severity="success">Health check passed</Alert>;
  }

  return (
    <Alert severity="error">
      <List sx={{ pt: 0 }}>
        {data!.getLinkShortenerDomain.healthCheckErrors.map((error, i) => (
          <ListItem key={i} sx={{ pt: 0 }}>
            {error}
          </ListItem>
        ))}
      </List>
    </Alert>
  );
}

function CompleteDNSSetup({
  domain,
  onClose,
}: {
  domain: Domain;
  onClose: () => void;
}): ReactElement {
  const formatErrorMessage = useErrorMessageFormatter();
  const [completeSetup, completeSetupResult] = useMutation<
    VerifyLinkShortenerDomainDnsAndCompleteSetupMutation,
    VerifyLinkShortenerDomainDnsAndCompleteSetupMutationVariables
  >(VERIFY_LINK_SHORTENER_DOMAIN_DNS_AND_COMPLETE_SETUP, {
    variables: {
      input: {
        linkShortenerDomainId: domain.id,
      },
    },
  });

  return (
    <>
      <DialogContent>
        {completeSetupResult.error ? (
          <Alert severity="error">
            {formatErrorMessage(completeSetupResult.error)}
          </Alert>
        ) : null}

        <Typography>
          DNS setup is complete. Next, configure the name servers (NS records)
          for the domain to point to the following:
        </Typography>
        <NameServerList nameservers={domain.nameservers} />
        <Typography>
          Once you have configured the name servers, wait a few minutes and
          click the button below to verify the configuration and complete setup.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => completeSetup()}
          disabled={completeSetupResult.loading}
        >
          {completeSetupResult.loading ? "Verifying..." : "Verify & Continue"}
        </Button>
      </DialogActions>
    </>
  );
}

function FinalLinkTestFailed({
  domain,
  onClose,
}: {
  domain: Domain;
  onClose: () => void;
}): ReactElement {
  const { enqueueSnackbar } = useSnackbar();
  const formatErrorMessage = useErrorMessageFormatter();
  const [retryTest, retryTestResult] = useMutation<
    RetryDomainFinalLinkTestMutation,
    RetryDomainFinalLinkTestMutationVariables
  >(RETRY_DOMAIN_FINAL_LINK_TEST, {
    variables: {
      input: {
        linkShortenerDomainId: domain.id,
      },
    },
    onCompleted: () => {
      enqueueSnackbar("Retest succeeded", { variant: "info" });
      onClose();
    },
  });

  return (
    <>
      <DialogContent>
        {retryTestResult.error ? (
          <Alert severity="error">
            {formatErrorMessage(retryTestResult.error)}
          </Alert>
        ) : null}

        <Typography>
          Our final end-to-end test of the domain failed. This can happen if the
          DNS settings were changed after verification in the previous step.
          Confirm that the DNS settings are correct:
        </Typography>
        <NameServerList nameservers={domain.nameservers} />
        <Typography>
          Once you have confirmed the name servers, wait a few minutes and click
          the button below to retry verification.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => retryTest()}
          disabled={retryTestResult.loading}
        >
          {retryTestResult.loading ? "Retrying..." : "Retry Verification"}
        </Button>
      </DialogActions>
    </>
  );
}

export default function UpdateDomainDialog({
  open,
  onClose,
  domain,
  onSubmitEdit,
}: {
  open: boolean;
  onClose: () => void;
  domain: Domain | null;
  onSubmitEdit: (
    values: UpdateLinkShortenerDomainMutationVariables
  ) => Promise<void>;
}): ReactElement {
  const isStaff = useCurrentUserIsStaff();

  function renderDialogBody() {
    if (!open || !domain) {
      return null;
    }

    const healthCheck = (
      <>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h2">Health Check (Staff Only)</Typography>
          <HealthCheck domain={domain} />
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h2">DNS Records (Staff Only)</Typography>
          <Typography>
            The domain must be configured to use the following DNS nameservers
            (NS records). This was verified during setup, but these records are
            provided here in case the configured is changed accidentally.
          </Typography>
          <NameServerList nameservers={domain.nameservers} />
        </Box>
      </>
    );

    if (domain.status === LinkShortenerDomainStatus.Ready) {
      // return <LinkShortenerEditFallbackUrl domain={domain} onClose={onClose} />;
      return (
        <RequestOrEditDomain
          domain={domain}
          open={open}
          onClose={onClose}
          onSubmitEdit={onSubmitEdit}
          healthCheck={healthCheck}
        />
      );
    }

    if (domain.status === LinkShortenerDomainStatus.Archived) {
      return (
        <ErrorDialogMessage
          message="This domain has been archived and cannot be edited."
          nameservers={domain.nameservers}
          onClose={onClose}
        />
      );
    }

    if (!isStaff) {
      // This shouldn't happen -- non-staff should only be able to open the edit
      // dialog for READY

      console.error(
        "Unexpected state: non-staff user editing domain that is not ready or archived",
        { domain, isStaff }
      );

      return (
        <ErrorDialogMessage
          message="This domain is not ready to use"
          onClose={onClose}
        />
      );
    }

    switch (domain.status) {
      case LinkShortenerDomainStatus.DnsOnlyStackCreating:
        return <LoadingDialog message="Setting up DNS..." onClose={onClose} />;
      case LinkShortenerDomainStatus.DnsOnlyStackFailed:
        return (
          <ErrorDialogMessage
            message="DNS setup failed"
            messageDetails={domain.statusMessage}
            onClose={onClose}
          />
        );
      case LinkShortenerDomainStatus.DnsOnlyStackReady:
        return <CompleteDNSSetup domain={domain} onClose={onClose} />;
      case LinkShortenerDomainStatus.FullStackCreating:
        return (
          <LoadingDialog
            message="Finishing Domain Setup..."
            onClose={onClose}
          />
        );
      case LinkShortenerDomainStatus.FullStackFailed:
        return (
          <ErrorDialogMessage
            message="Domain setup failed"
            messageDetails={domain.statusMessage}
            nameservers={domain.nameservers}
            onClose={onClose}
          />
        );
      case LinkShortenerDomainStatus.FullStackLinkTestFailed:
        return <FinalLinkTestFailed domain={domain} onClose={onClose} />;
    }

    return null;
  }

  return (
    <Dialog
      open={open && !!domain}
      onClose={onClose}
      maxWidth={
        domain?.status === LinkShortenerDomainStatus.Ready && !isStaff
          ? "sm"
          : "md"
      }
      fullWidth
    >
      <DialogTitle>Update Domain</DialogTitle>
      {renderDialogBody()}
    </Dialog>
  );
}
